import { Sim } from '../../game/simulation';
import { ICON } from '../icons';

/**
 * Always-on Storage panel pinned to the bottom-right corner. Same
 * visual chrome as the Storage modal (header bar + sectioned body),
 * but the actual inventory contents are rendered as **chips** — small
 * icon-and-count tokens — instead of label/value rows, so the player
 * sees their stockpile at a glance without needing to open a modal.
 *
 * Every chip carries `data-id="<resource>"` matching the FlyToHud
 * target lookup so flying resource animations still land on the right
 * chip. Chips for never-owned resources stay hidden with `.never-seen`;
 * once seen they remain mounted and fade via `.is-empty` when their
 * count returns to 0 — the layout doesn't jitter as resources come
 * and go.
 *
 * StorageBar subscribes to the bus BEFORE FlyToHud so a fly-landing
 * always has a real visible target by the time the resolver runs.
 */
type Chip = {
  id: string;
  read: (r: any) => number;
  icon: string;
  title: string;
};
type Section = { title: string; chips: Chip[] };

const SECTIONS: Section[] = [
  {
    title: 'Breakdown',
    chips: [
      { id: 'basket', read: r => r.basket, icon: ICON.wheat,  title: 'Wheat' },
      { id: 'corn',   read: r => r.corn,   icon: ICON.corn,   title: 'Corn' },
      { id: 'carrot', read: r => r.carrot, icon: ICON.carrot, title: 'Carrot' },
    ],
  },
  {
    title: 'Seeds',
    chips: [
      { id: 'seedWheat',  read: r => r.seeds?.wheat  ?? 0, icon: ICON.wheat,  title: 'Wheat seeds' },
      { id: 'seedCorn',   read: r => r.seeds?.corn   ?? 0, icon: ICON.corn,   title: 'Corn seeds' },
      { id: 'seedCarrot', read: r => r.seeds?.carrot ?? 0, icon: ICON.carrot, title: 'Carrot seeds' },
    ],
  },
  {
    title: 'Milled',
    chips: [
      { id: 'flour',     read: r => r.flour,          icon: ICON.flour, title: 'Flour' },
      { id: 'cornFlour', read: r => r.cornFlour ?? 0, icon: ICON.flour, title: 'Corn flour' },
    ],
  },
  {
    title: 'Materials',
    chips: [
      { id: 'wood',  read: r => r.wood,       icon: ICON.wood, title: 'Wood' },
      { id: 'stone', read: r => r.stone ?? 0, icon: ICON.stone, title: 'Stone' },
    ],
  },
  {
    title: 'Foraged',
    chips: [
      { id: 'treeSeeds', read: r => r.treeSeeds ?? 0, icon: ICON.acorn,     title: 'Acorns' },
      { id: 'mushroom',  read: r => r.mushrooms ?? 0, icon: ICON.mushroom,  title: 'Mushrooms' },
      { id: 'berry',     read: r => r.berries ?? 0,   icon: ICON.berry,     title: 'Berries' },
    ],
  },
];

export class StorageBar {
  private root: HTMLElement;
  private everSeen = new Set<string>();
  private lastHash = '';

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('aside');
    this.root.className = 'ui-storagebar';
    this.buildStructure();
    parent.appendChild(this.root);
    this.markCurrentlySeen();
    // Register on the bus BEFORE FlyToHud so the chip is unhidden in
    // time for target() to resolve a real screen position.
    sim.bus.on(() => this.markCurrentlySeen());
    this.update();
  }

  private buildStructure() {
    const sectionsHTML = SECTIONS.map(sec => {
      const chipsHTML = sec.chips.map(c => `
        <div class="ui-chip ui-chip--inv never-seen" data-id="${c.id}" title="${c.title}">
          <span class="ui-chip__icon glyph">${c.icon}</span>
          <span class="ui-chip__value" data-field="${c.id}">0</span>
        </div>
      `).join('');
      return `
        <div class="ui-storagebar__section" data-section="${sec.title.toLowerCase()}">
          <div class="ui-storagebar__sectitle">${sec.title}</div>
          <div class="ui-storagebar__chips">${chipsHTML}</div>
        </div>
      `;
    }).join('');
    this.root.innerHTML = `
      <header class="ui-storagebar__header">
        <span class="glyph glyph-inline">${ICON.storage}</span>
        <h2 class="ui-storagebar__title">Storage</h2>
      </header>
      <div class="ui-storagebar__body">
        <div class="ui-storagebar__capwrap">
          <div class="ui-storagebar__sectitle">All crops</div>
          <div class="ui-bar">
            <div class="ui-bar__fill" data-field="capfill" style="width:0%"></div>
            <div class="ui-bar__label" data-field="caplabel">0 / 0</div>
          </div>
        </div>
        ${sectionsHTML}
      </div>
    `;
  }

  private markCurrentlySeen() {
    const s = this.sim.state.res;
    for (const sec of SECTIONS) {
      let unhid = false;
      for (const c of sec.chips) {
        if (c.read(s) > 0 && !this.everSeen.has(c.id)) {
          this.everSeen.add(c.id);
          const chip = this.root.querySelector(`[data-id="${c.id}"]`) as HTMLElement | null;
          if (chip) chip.classList.remove('never-seen');
          unhid = true;
        }
      }
      // If we just revealed the first chip in this section, also unhide
      // the section container — otherwise its display:none cascades down
      // and the chip stays invisible until update() ticks next frame.
      // That delay made FlyToHud's target() resolve to 0×0 and bounce
      // the animation to the top-right fallback instead of the actual
      // (about-to-appear) chip.
      if (unhid) {
        const secEl = this.root.querySelector(`[data-section="${sec.title.toLowerCase()}"]`) as HTMLElement | null;
        if (secEl) secEl.classList.remove('never-seen');
      }
    }
  }

  update() {
    const res = this.sim.state.res;
    const inPlot = !!this.sim.state.plotInView;
    const parts: string[] = [];
    for (const sec of SECTIONS) {
      for (const c of sec.chips) {
        parts.push(`${c.id}:${c.read(res)}:${this.everSeen.has(c.id) ? 1 : 0}`);
      }
    }
    const rep = this.sim.storage();
    parts.push(`cap:${rep.totalUsed}/${rep.totalCap}`);
    parts.push(`plot:${inPlot ? 1 : 0}`);
    const hash = parts.join('|');
    if (hash === this.lastHash) return;
    this.lastHash = hash;
    // Plot-mode filter: woodland, build, and milled goods are irrelevant
    // when the player is focused on a plot. Hide those sections so the
    // storage panel reads as a gardening-specific inventory.
    const plotHidden = new Set(['Milled', 'Materials']);
    for (const sec of SECTIONS) {
      const secEl = this.root.querySelector(`[data-section="${sec.title.toLowerCase()}"]`) as HTMLElement | null;
      if (secEl) secEl.classList.toggle('plot-hidden', inPlot && plotHidden.has(sec.title));
    }
    // Cap bar.
    const pct = rep.totalCap > 0 ? Math.min(100, Math.round((rep.totalUsed / rep.totalCap) * 100)) : 0;
    const fill = this.root.querySelector('[data-field="capfill"]') as HTMLElement | null;
    const lbl  = this.root.querySelector('[data-field="caplabel"]') as HTMLElement | null;
    if (fill) fill.style.width = `${pct}%`;
    if (lbl)  lbl.textContent = `${rep.totalUsed} / ${rep.totalCap} (${pct}%)`;
    // Chips + section visibility.
    for (const sec of SECTIONS) {
      let anySeen = false;
      for (const c of sec.chips) {
        const count = c.read(res);
        const chip = this.root.querySelector(`[data-id="${c.id}"]`) as HTMLElement | null;
        const valEl = this.root.querySelector(`[data-field="${c.id}"]`);
        // Display integers as-is; if a future system introduces fractional
        // resources, show one decimal so 0.5 doesn't display as 0.
        if (valEl) {
          valEl.textContent = Number.isInteger(count)
            ? `${count}`
            : count.toFixed(1);
        }
        if (!chip) continue;
        const seen = this.everSeen.has(c.id);
        chip.classList.toggle('never-seen', !seen);
        chip.classList.toggle('is-empty', count <= 0 && seen);
        if (seen) anySeen = true;
      }
      // Hide the whole section (header + chips) when nothing in it has
      // ever been seen — fresh players don't see empty "Foraged" /
      // "Milled" / "Materials" headers before they've found those goods.
      const secEl = this.root.querySelector(`[data-section="${sec.title.toLowerCase()}"]`) as HTMLElement | null;
      if (secEl) secEl.classList.toggle('never-seen', !anySeen);
    }
  }
}
